<script setup lang="ts">
import { useI18n } from '@/composables/useI18n'
import { useFeatureStore } from '@/stores/feature'
import { ShieldCheck, Lock, Mail, MapPin, BadgeCheck } from 'lucide-vue-next'
import BrandMark from '@/components/icons/BrandMark.vue'
import { useThemeStore } from '@/stores/theme'
import TundukMark from '@/components/icons/TundukMark.vue'

const { t } = useI18n()
const themeStore = useThemeStore()
const featureStore = useFeatureStore()
const currentYear = new Date().getFullYear()

const footerLinks = {
  platform: [
    { label: 'footer.allAuctions', path: '/auctions' },
    { label: 'footer.allCategories', path: '/categories' },
    { label: 'footer.howItWorks', path: '/how-it-works' },
    { label: 'nav.sell', path: '/sell' },
  ],
  company: [
    { label: 'footer.aboutUs', path: '/about' },
    { label: 'footer.contact', path: '/contact' },
  ],
  legal: [
    { label: 'footer.terms', path: '/terms' },
    { label: 'footer.privacy', path: '/privacy' },
  ],
}

// Footer links used to be bare inline text (17px tall), far under the 40px
// minimum tap target — and they sit in the part of the page a thumb reaches
// last. The negative margin keeps the text optically flush with the column
// heading while the padding grows the hit box.
const columnLinkClass =
  '-mx-2 flex min-h-[40px] items-center rounded-lg px-2 text-sm text-white/75 transition hover:bg-white/[0.06] hover:text-primaryOnDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:min-h-[34px]'

// Same idea in the bottom bar, but the negative margin lives on the row so the
// boxes cannot overlap each other; the row's edges stay optically aligned with
// the copyright line above it.
const barLinkClass =
  'inline-flex min-h-[40px] items-center rounded-lg px-2 transition hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:min-h-[32px]'
</script>

<template>
  <footer class="relative overflow-hidden bg-ink text-white w-full max-w-[1440px] mx-auto border-t border-white/10" :aria-label="t('footer.ariaLabel') || 'Подвал сайта'">
    <TundukMark class="pointer-events-none absolute -left-12 -top-10 h-80 w-80 text-white/[0.04]" />

    <!-- Trust band -->
    <div class="relative border-b border-white/10">
      <div class="mx-auto flex w-full max-w-[1440px] flex-col gap-4 px-4 py-6 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div class="flex items-center gap-3">
          <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primaryOnDark/15 text-primaryOnDark"><ShieldCheck class="h-5 w-5" /></span>
          <div>
            <p class="text-sm font-semibold text-white">{{ t('footer.nbkrTitle') || 'Защищённые сделки' }}</p>
            <p class="text-xs text-white/55">{{ t('footer.nbkrDesc') || 'SSL-шифрование соединения и защита сделок iTorgo Escrow' }}</p>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <template v-if="featureStore.activeBanks.length > 0">
            <span v-for="bank in featureStore.activeBanks" :key="bank.id" class="rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-white">{{ bank.name }}</span>
          </template>
          <template v-else>
            <span class="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-white/85"><Lock class="h-3.5 w-3.5 text-primaryOnDark" />SSL</span>
            <span class="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-white/85"><ShieldCheck class="h-3.5 w-3.5 text-primaryOnDark" />iTorgo Escrow</span>
            <span class="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-white/85"><BadgeCheck class="h-3.5 w-3.5 text-primaryOnDark" />KYC</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Columns -->
    <div class="relative mx-auto w-full max-w-[1440px] px-4 py-12 sm:px-6 lg:px-8">
      <div class="grid grid-cols-2 gap-8 md:grid-cols-5">
        <div class="col-span-2 space-y-4">
          <router-link to="/" class="group inline-flex items-center gap-2.5" :aria-label="t('footer.homeAria') || 'iTorgo Главная'">
            <img
              v-if="themeStore.theme.logoType === 'image' && themeStore.theme.logoUrl"
              :src="themeStore.theme.logoUrl"
              :alt="themeStore.theme.logoText || 'iTorgo'"
              :style="{ height: (themeStore.theme.logoHeightPx || 36) + 'px' }"
              class="w-auto object-contain"
            />
            <template v-else>
              <BrandMark v-if="themeStore.theme.logoType !== 'text_only'" ground="dark" class="h-10 w-10 p-1.5 transition-transform group-hover:scale-105" />
              <span class="font-display text-xl font-bold tracking-tight">{{ themeStore.theme.logoText || 'iTorgo' }}</span>
            </template>
          </router-link>
          <p class="max-w-sm text-sm leading-relaxed text-white/60">{{ t('footer.tagline') }}</p>
          <div class="pt-1 text-sm text-white/60">
            <div class="flex min-h-[36px] items-center gap-2"><MapPin class="h-3.5 w-3.5 shrink-0 text-white/40" /><span>{{ t('footer.location') || 'Бишкек, Кыргызская Республика' }}</span></div>
            <a href="mailto:info@itorgo.com" class="-mx-2 flex min-h-[40px] w-fit items-center gap-2 rounded-lg px-2 transition hover:bg-white/[0.06] hover:text-primaryOnDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><Mail class="h-3.5 w-3.5 shrink-0 text-white/40" /><span>info@itorgo.com</span></a>
          </div>
        </div>

        <nav :aria-label="t('footer.platform') || 'Платформа'" class="space-y-3">
          <h2 class="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">{{ t('footer.platform') || 'Платформа' }}</h2>
          <ul class="space-y-0.5">
            <li v-for="link in footerLinks.platform" :key="link.path"><router-link :to="link.path" :class="columnLinkClass">{{ t(link.label) }}</router-link></li>
          </ul>
        </nav>
        <nav :aria-label="t('footer.company') || 'Компания'" class="space-y-3">
          <h2 class="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">{{ t('footer.company') || 'Компания' }}</h2>
          <ul class="space-y-0.5">
            <li v-for="link in footerLinks.company" :key="link.path"><router-link :to="link.path" :class="columnLinkClass">{{ t(link.label) }}</router-link></li>
          </ul>
        </nav>
        <nav :aria-label="t('footer.legal') || 'Юридическая информация'" class="space-y-3">
          <h2 class="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">{{ t('footer.legal') || 'Юридическая информация' }}</h2>
          <ul class="space-y-0.5">
            <li v-for="link in footerLinks.legal" :key="link.path"><router-link :to="link.path" :class="columnLinkClass">{{ t(link.label) }}</router-link></li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="relative border-t border-white/10">
      <div class="mx-auto flex w-full max-w-[1440px] flex-col gap-1 px-4 py-4 text-xs text-white/50 sm:px-6 md:flex-row md:items-center md:justify-between md:gap-3 lg:px-8">
        <p>&copy; {{ currentYear }} iTorgo. {{ t('footer.copyright') || 'Все права защищены. Кыргызстан, Бишкек.' }}</p>
        <div class="-mx-2 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <router-link v-for="link in footerLinks.legal" :key="link.path" :to="link.path" :class="barLinkClass">{{ t(link.label) }}</router-link>
          <span class="hidden text-white/20 md:inline" aria-hidden="true">|</span>
          <span class="px-2 font-display font-semibold text-white/70">{{ t('footer.currencyLabel') || 'KGS (сом)' }}</span>
        </div>
      </div>
    </div>
  </footer>
</template>
